import { ArchiveIcon, EditIcon } from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { FormDetailPane } from '@/components/common/FormDetailPane';
import { SelectFormField, TextFormField } from '@/components/common/FormField';
import { Button } from '@/components/ui/button';
import { FieldGroup } from '@/components/ui/field';
import type { Employee } from '../data/databaseSchema';
import { employeeService } from '../services/employeeService';

interface EmployeeFormData {
  employee_id: string;
  fullname: string;
  email: string;
  role: string;
  status: string;
}

interface EmployeeDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (employee: Employee) => void;
  onEntrySaved?: () => void;
  employee?: Employee | null;
  isEdit?: boolean;
  readOnly?: boolean;
  onEditRequest?: () => void;
  onDeleteRequest?: () => void;
  isDeleteDisabled?: boolean;
}

export const EmployeeDrawer: React.FC<EmployeeDrawerProps> = ({
  isOpen,
  onClose,
  onSave,
  onEntrySaved,
  employee,
  isEdit = false,
  readOnly = false,
  onEditRequest,
  onDeleteRequest,
  isDeleteDisabled = false,
}) => {
  const [formData, setFormData] = useState<EmployeeFormData>({
    employee_id: '',
    fullname: '',
    email: '',
    role: 'user',
    status: 'active',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if ((isEdit || readOnly) && employee) {
        setFormData({
          employee_id: employee.employee_id || '',
          fullname: employee.fullname || '',
          email: employee.email || '',
          role: employee.role || 'user',
          status: employee.status || 'active',
        });
      } else {
        setFormData({
          employee_id: '',
          fullname: '',
          email: '',
          role: 'user',
          status: 'active',
        });
      }
      setErrors({});
      setSubmitError(null);
    }
  }, [isOpen, isEdit, readOnly, employee]);

  const handleInputChange = (field: keyof EmployeeFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.employee_id || formData.employee_id.trim() === '') {
      newErrors.employee_id = 'Employee ID is required';
    }

    if (!formData.fullname || formData.fullname.trim() === '') {
      newErrors.fullname = 'Full name is required';
    }

    if (!formData.email || formData.email.trim() === '') {
      newErrors.email = 'Email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const apiData = {
        employee_id: formData.employee_id.trim(),
        fullname: formData.fullname.trim(),
        email: formData.email.trim(),
        role: formData.role,
        status: formData.status,
      };

      const response =
        isEdit && employee
          ? await employeeService.updateEmployee(employee.id, apiData)
          : await employeeService.createEmployee(apiData);

      onSave(response);
      onEntrySaved?.();
      onClose();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldsDisabled = readOnly || isEdit;

  return (
    <FormDetailPane
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={readOnly ? 'View Employee' : isEdit ? 'Edit Employee' : 'Add Employee'}
      onSubmit={readOnly ? undefined : handleSubmit}
      submitLabel={isEdit ? 'Update Employee' : 'Add Employee'}
      isSubmitting={isSubmitting}
      submitError={submitError}
      showFooter={!readOnly}
    >
      {readOnly ? (
        <div className="flex flex-wrap gap-2 pb-2 justify-end">
          {onEditRequest ? (
            <Button type="button" size="sm" onClick={onEditRequest}>
              <EditIcon data-icon="inline-start" />
              Edit
            </Button>
          ) : null}
          {onDeleteRequest ? (
            <Button
              type="button"
              size="sm"
              variant="destructive"
              disabled={isDeleteDisabled}
              onClick={onDeleteRequest}
            >
              <ArchiveIcon data-icon="inline-start" />
              Archive
            </Button>
          ) : null}
        </div>
      ) : null}
      <FieldGroup>
        <TextFormField
          label="Employee ID"
          required
          value={formData.employee_id}
          onChange={(v) => handleInputChange('employee_id', v)}
          error={errors.employee_id}
          disabled={fieldsDisabled}
          placeholder="Enter employee ID"
          copyable
        />
        <TextFormField
          label="Full Name"
          required
          value={formData.fullname}
          onChange={(v) => handleInputChange('fullname', v)}
          error={errors.fullname}
          disabled={readOnly}
          placeholder="Enter full name"
          copyable
        />
        <TextFormField
          label="Email"
          type="email"
          required
          value={formData.email}
          onChange={(v) => handleInputChange('email', v)}
          error={errors.email}
          disabled={readOnly}
          placeholder="Enter email address"
          copyable
        />
        <SelectFormField
          label="Role"
          required
          value={formData.role}
          onChange={(v) => handleInputChange('role', v)}
          disabled={readOnly}
          copyable
          options={[
            { value: 'user', label: 'User' },
            { value: 'admin', label: 'Admin' },
          ]}
        />
        <SelectFormField
          label="Status"
          required
          value={formData.status}
          onChange={(v) => handleInputChange('status', v)}
          disabled={readOnly}
          copyable
          options={[
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
          ]}
        />
      </FieldGroup>
    </FormDetailPane>
  );
};
